// src/pages/Login.js
import React, { useState, useContext } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import { AuthContext } from "../context/AuthContext";
import Button from "../components/ui/Button";
import { showToast } from "../components/ui/toast";
import logo from "../assets/images/logo.png";
import { loginApi, verifyMfaApi, verifyBackupCodeApi } from "apis/auth";

// Two steps, because sign-in has two outcomes now:
//
//   - the admin is enrolled → the password buys a challenge, not a session,
//     and a code from their authenticator finishes the job
//   - the admin is not enrolled yet → they get a session that can do nothing
//     except reach /mfa-setup, which is where they're sent
const Login = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const [step, setStep] = useState("credentials");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [backendError, setBackendError] = useState("");

  const credentialsForm = useForm({ defaultValues: { email: "", password: "" } });
  const codeForm = useForm({ defaultValues: { code: "" } });

  // Sends them wherever their account is actually ready to go. Keyed on
  // twoFactorEnabled rather than on whether /me returned something — /me
  // answers 200 for an un-enrolled admin too.
  const finishSignIn = async () => {
    const me = await login();
    showToast("login", "Login Successfully");
    // mfaSkipped: the backend's local-dev 2FA skip (ADMIN_SKIP_MFA) is on.
    navigate(me?.twoFactorEnabled || me?.mfaSkipped ? "/" : "/mfa-setup");
  };

  const onSubmitCredentials = async (data) => {
    setLoading(true);
    setBackendError("");
    try {
      const res = await loginApi({
        email: data.email?.trim().toLowerCase(),
        password: data.password,
      });

      if (res.data?.twoFactorRequired) {
        setStep("code");
        return;
      }

      await finishSignIn();
    } catch (error) {
      setBackendError(
        error.response?.data?.message ||
          error.message ||
          "An unexpected error occurred",
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmitCode = async ({ code }) => {
    setLoading(true);
    setBackendError("");
    try {
      const trimmed = code.trim();
      if (useBackupCode) {
        await verifyBackupCodeApi(trimmed);
      } else {
        await verifyMfaApi(trimmed);
      }
      await finishSignIn();
    } catch (error) {
      setBackendError(
        error.response?.data?.message ||
          "That code isn't right. Check your authenticator and retry.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Back to the start — the challenge cookie is short-lived, so a fresh
  // password entry is the reliable way to restart.
  const startOver = () => {
    setStep("credentials");
    setUseBackupCode(false);
    setBackendError("");
    codeForm.reset();
  };

  const errorBanner = backendError ? (
    <div className="text-red-500 text-center mb-4 p-4 bg-red-50 border border-red-200 rounded">
      {backendError}
    </div>
  ) : null;

  return (
    <div className="min-h-screen py-8 px-4 flex items-center justify-center">
      {step === "credentials" ? (
        <form
          className="border border-mediumgray p-8 rounded-md w-full max-w-md"
          onSubmit={credentialsForm.handleSubmit(onSubmitCredentials)}
        >
          <div className="flex justify-center items-center w-full pb-8">
            <img src={logo} alt="Beleef" className="w-[150px] h-auto" />
          </div>

          <h4 className="text-center">Admin Login</h4>
          {errorBanner}

          <div className="my-4">
            <input
              type="email"
              autoComplete="username"
              placeholder="Email"
              className={`form-input border ${
                credentialsForm.formState.errors.email
                  ? "border-red-500"
                  : "border-mediumgray"
              }`}
              {...credentialsForm.register("email", {
                required: "Email is required",
                pattern: { value: /^\S+@\S+$/i, message: "Invalid email" },
              })}
            />
            {credentialsForm.formState.errors.email && (
              <p className="form-error-message">
                {credentialsForm.formState.errors.email.message}
              </p>
            )}
          </div>

          <div className="mb-6">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Password"
                className={`form-input border ${
                  credentialsForm.formState.errors.password
                    ? "border-red-500"
                    : "border-mediumgray"
                }`}
                {...credentialsForm.register("password", {
                  required: "Password is required",
                })}
              />
              <span
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
              >
                <i
                  className={`fa-regular ${
                    showPassword ? "fa-eye" : "fa-eye-slash"
                  } text-gray-400`}
                ></i>
              </span>
            </div>
            {credentialsForm.formState.errors.password && (
              <p className="form-error-message">
                {credentialsForm.formState.errors.password.message}
              </p>
            )}
          </div>

          <div className="text-right mb-4">
            <Link to="/forgot-password" className="text-blue-600 hover:underline">
              Forgot Password?
            </Link>
          </div>

          <Button
            type="submit"
            className="w-full black-button"
            loading={loading}
            disabled={loading}
          >
            Login
          </Button>
        </form>
      ) : (
        <form
          className="border border-mediumgray p-8 rounded-md w-full max-w-md"
          onSubmit={codeForm.handleSubmit(onSubmitCode)}
        >
          <div className="flex justify-center items-center w-full pb-8">
            <img src={logo} alt="Beleef" className="w-[150px] h-auto" />
          </div>

          <h4 className="text-center">
            {useBackupCode ? "Enter a backup code" : "Enter your code"}
          </h4>
          <p className="text-sm text-darkergray text-center mt-2 mb-4">
            {useBackupCode
              ? "Use one of the codes you saved when you set up two-factor. Each works once."
              : "Open your authenticator app and enter the 6-digit code."}
          </p>

          {errorBanner}

          <div className="mb-6">
            <input
              type="text"
              autoFocus
              inputMode={useBackupCode ? "text" : "numeric"}
              autoComplete="one-time-code"
              maxLength={useBackupCode ? 32 : 6}
              placeholder={useBackupCode ? "backup code" : "000000"}
              className={`form-input border text-center text-lg ${
                useBackupCode ? "tracking-widest" : "tracking-[0.5em]"
              } border-mediumgray`}
              {...codeForm.register("code", {
                required: useBackupCode
                  ? "Enter a backup code"
                  : "Enter the 6-digit code",
                ...(useBackupCode
                  ? {}
                  : {
                      pattern: {
                        value: /^\d{6}$/,
                        message: "Six digits, numbers only",
                      },
                    }),
              })}
            />
            {codeForm.formState.errors.code && (
              <p className="form-error-message">
                {codeForm.formState.errors.code.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full black-button"
            loading={loading}
            disabled={loading}
          >
            Verify
          </Button>

          <div className="flex justify-between mt-4 text-sm">
            <button
              type="button"
              className="text-blue-600 hover:underline"
              onClick={() => {
                setUseBackupCode((prev) => !prev);
                setBackendError("");
                codeForm.reset();
              }}
            >
              {useBackupCode ? "Use my authenticator" : "Use a backup code"}
            </button>
            <button
              type="button"
              className="text-darkergray underline"
              onClick={startOver}
            >
              Start over
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Login;
